import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics.service';
import {
  EngagementMetrics,
  BehavioralMetrics,
  KpiQueryParams,
  MonthlyCompanyTransferPatterns,
} from '../../types/kpi.types';
import { LineChart, BarChart, MetricCard, GaugeChart, HeatmapChart } from '../Charts';
import { Loader } from '../Loader';

interface TransferPatternsProps {
  queryParams: KpiQueryParams;
}

export const TransferPatterns: React.FC<TransferPatternsProps> = ({
  queryParams,
}) => {
  const [engagement, setEngagement] = useState<EngagementMetrics | null>(null);
  const [behavioral, setBehavioral] = useState<BehavioralMetrics | null>(
    null,
  );
  const [monthlyTransferData, setMonthlyTransferData] = useState<MonthlyCompanyTransferPatterns[]>([]);
  const [transfersByDayOfMonth, setTransfersByDayOfMonth] = useState<Array<{ day: number; avgTransfers: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const formatMonth = (month: string): string => {
    const [year, m] = month.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(m) - 1]} '${year.slice(2)}`;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [eng, beh, monthlyData, dayOfMonthData] = await Promise.all([
          analyticsService.getEngagementMetrics(queryParams),
          analyticsService.getBehavioralMetrics(queryParams),
          analyticsService.getMonthlyCompanyTransferPatterns(queryParams),
          analyticsService.getTransfersByDayOfMonth(queryParams),
        ]);
        setEngagement(eng);
        setBehavioral(beh);
        setMonthlyTransferData(monthlyData);
        setTransfersByDayOfMonth(dayOfMonthData);
      } catch (err: any) {
        setError(err.message || 'Failed to load transfer data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [queryParams.startDate, queryParams.endDate, queryParams.database, queryParams.companyId]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <div style={{ color: 'red' }}>Error: {error}</div>;
  }

  if (!engagement || !behavioral) {
    return <div>No data available</div>;
  }

  // Filter monthly data by selected company (from global selector)
  const filteredMonthlyData = queryParams.companyId
    ? monthlyTransferData.filter(d => d.companyId.toString() === queryParams.companyId)
    : monthlyTransferData;

  // Calculate monthly totals (aggregate across all companies when "all" is selected)
  const monthlyTotalsMap = new Map<string, {
    month: string;
    totalTransfers: number;
    totalSuccessful: number;
    totalFailed: number;
    totalDeclined: number; // Only status 100 (DECLINED)
    totalAmount: number;
    avgTransferAmount: number;
    transferVelocity: number;
    successRate: number;
    declineRate: number;
    status0Count: number;
    status100Count: number;
    status200Count: number;
    status300Count: number;
    status400Count: number;
    status0Percent: number;
    status100Percent: number;
    status200Percent: number;
    status300Percent: number;
    status400Percent: number;
  }>();

  filteredMonthlyData.forEach(row => {
    if (!monthlyTotalsMap.has(row.month)) {
      monthlyTotalsMap.set(row.month, {
        month: row.month,
        totalTransfers: 0,
        totalSuccessful: 0,
        totalFailed: 0,
        totalDeclined: 0,
        totalAmount: 0,
        avgTransferAmount: 0,
        transferVelocity: 0,
        successRate: 0,
        declineRate: 0,
        status0Count: 0,
        status100Count: 0,
        status200Count: 0,
        status300Count: 0,
        status400Count: 0,
        status0Percent: 0,
        status100Percent: 0,
        status200Percent: 0,
        status300Percent: 0,
        status400Percent: 0,
      });
    }
    const totals = monthlyTotalsMap.get(row.month)!;
    totals.totalTransfers += row.totalTransfers;
    totals.totalSuccessful += row.successfulTransfers;
    totals.totalFailed += row.failedTransfers;
    // Calculate declined count from declineRate percentage: declined = (declineRate / 100) * totalTransfers
    totals.totalDeclined += (row.declineRate / 100) * row.totalTransfers;
    totals.totalAmount += row.totalAmount;
    totals.status0Count += row.status0Count;
    totals.status100Count += row.status100Count;
    totals.status200Count += row.status200Count;
    totals.status300Count += row.status300Count;
    totals.status400Count += row.status400Count;
  });

  // Calculate averages for totals
  monthlyTotalsMap.forEach((totals, month) => {
    totals.successRate = totals.totalTransfers > 0 
      ? Math.round((totals.totalSuccessful / totals.totalTransfers) * 100 * 100) / 100
      : 0;
    // Decline rate should be calculated from actual declined count (status 100), not total failed
    totals.declineRate = totals.totalTransfers > 0
      ? Math.round((totals.totalDeclined / totals.totalTransfers) * 100 * 100) / 100
      : 0;
    totals.avgTransferAmount = totals.totalSuccessful > 0
      ? Math.round((totals.totalAmount / totals.totalSuccessful) * 100) / 100
      : 0;
    
    // Calculate velocity: total amount per day in that month
    const [year, m] = month.split('-');
    const daysInMonth = new Date(parseInt(year), parseInt(m), 0).getDate();
    totals.transferVelocity = daysInMonth > 0 
      ? Math.round((totals.totalAmount / daysInMonth) * 100) / 100
      : 0;
    
    // Calculate status percentages as % of total failed transfers
    const totalFailed = totals.status0Count + totals.status100Count + totals.status200Count + totals.status300Count + totals.status400Count;
    totals.status0Percent = totalFailed > 0 ? Math.round((totals.status0Count / totalFailed) * 100 * 100) / 100 : 0;
    totals.status100Percent = totalFailed > 0 ? Math.round((totals.status100Count / totalFailed) * 100 * 100) / 100 : 0;
    totals.status200Percent = totalFailed > 0 ? Math.round((totals.status200Count / totalFailed) * 100 * 100) / 100 : 0;
    totals.status300Percent = totalFailed > 0 ? Math.round((totals.status300Count / totalFailed) * 100 * 100) / 100 : 0;
    totals.status400Percent = totalFailed > 0 ? Math.round((totals.status400Count / totalFailed) * 100 * 100) / 100 : 0;
  });

  const monthlyTotals = Array.from(monthlyTotalsMap.values())
    .sort((a, b) => a.month.localeCompare(b.month));

  // For display: use totals if no company selected, otherwise use company-specific data
  const displayMonthlyData = !queryParams.companyId
    ? monthlyTotals.map(t => ({
        month: t.month,
        transferSuccessRate: t.successRate,
        totalTransfers: t.totalTransfers,
        successfulTransfers: t.totalSuccessful,
        failedTransfers: t.totalFailed,
        avgTransferAmount: t.avgTransferAmount,
        totalAmount: t.totalAmount,
        transferVelocity: t.transferVelocity,
        declineRate: t.declineRate,
        status0Percent: t.status0Percent,
        status100Percent: t.status100Percent,
        status200Percent: t.status200Percent,
        status300Percent: t.status300Percent,
        status400Percent: t.status400Percent,
      }))
    : filteredMonthlyData
        .sort((a, b) => a.month.localeCompare(b.month));

  // Create trend data for average transfer amount from monthly data
  const amountTrend = displayMonthlyData.map(row => ({
    date: row.month,
    value: row.avgTransferAmount,
  }));

  // Create velocity trend from monthly data
  const velocityTrend = displayMonthlyData.map(row => ({
    date: row.month,
    value: row.transferVelocity,
  }));

  // Transform peak hours for heatmap - format as bins (e.g., "13:00-14:00")
  const peakHoursData = behavioral.peakTransferHours.map((h) => {
    const nextHour = (h.hour + 1) % 24;
    const startTime = `${h.hour.toString().padStart(2, '0')}:00`;
    const endTime = `${nextHour.toString().padStart(2, '0')}:00`;
    return {
      x: `${startTime}-${endTime}`,
      y: '',
      value: h.count,
    };
  });


  // Transfers by day of month data
  const dayOfMonthData = transfersByDayOfMonth.map(d => ({
    day: `Day ${d.day}`,
    transfers: d.avgTransfers,
  }));

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>
          💸 Transfer Patterns & Performance
        </h2>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
          Analyze transfer success rates, amounts, timing, and distribution patterns
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <div>
          <GaugeChart
            value={behavioral.transferSuccessRate}
            max={100}
            title="Transfer Success Rate"
            threshold={{ warning: 85, critical: 70 }}
          />
        </div>
        <MetricCard
          title="Avg Transfer Amount"
          value={`₪${(engagement.avgTransferAmount || 0).toFixed(2)}`}
          subtitle="Per transaction"
          color="#4CAF50"
        />
        <MetricCard
          title="Transfer Velocity"
          value={`₪${(behavioral.transferVelocity || 0).toFixed(2)}`}
          subtitle="Per day"
          color="#82ca9d"
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <div>
          <LineChart
            data={amountTrend}
            title="Average Transfer Amount Trend"
            color="#4CAF50"
            height={250}
          />
          <div style={{ 
            marginTop: '12px', 
            padding: '12px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            fontSize: '13px',
            color: '#555',
          }}>
            <strong>Average Transfer Amount:</strong> Shows the average amount per successful transfer each month. Helps identify trends in transaction sizes - are users transferring larger or smaller amounts over time?
          </div>
        </div>
        <div>
          <LineChart
            data={velocityTrend}
            title="Transfer Velocity Trend"
            color="#82ca9d"
            height={250}
          />
          <div style={{ 
            marginTop: '12px', 
            padding: '12px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            fontSize: '13px',
            color: '#555',
          }}>
            <strong>Transfer Velocity:</strong> Shows the average amount transferred per day in each month. This metric indicates daily transaction volume and helps track growth or decline in financial activity over time.
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <HeatmapChart
          data={peakHoursData}
          title="Peak Transfer"
          xLabel=""
          yLabel="Count"
          height={300}
        />
      </div>

      <div style={{ marginBottom: '30px' }}>
        <BarChart
          data={dayOfMonthData}
          title="Average Transfers by Day of Month (averaged across all months in period)"
          dataKey="transfers"
          xAxisKey="day"
          color="#4CAF50"
          height={300}
        />
      </div>

      {/* Monthly Transfer Patterns Table */}
      {displayMonthlyData.length > 0 && (
        <div style={{
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginBottom: '24px',
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            📅 Monthly Transfer Patterns Breakdown
          </h3>
          
          {/* Explanation Box */}
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#555',
          }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', color: '#333' }}>📖 Understanding Transfer Statuses:</div>
            <div style={{ lineHeight: '1.8' }}>
              <div style={{ marginBottom: '6px' }}>
                <strong>✅ Successful Transfers:</strong> Status 500 (TRANSFERRED) - Money was successfully sent to the employee's bank account.
              </div>
              <div style={{ marginBottom: '6px' }}>
                <strong>❌ Failed Transfers</strong> include all transfers that didn't succeed, which can be:
              </div>
              <ul style={{ margin: '4px 0 6px 20px', padding: 0 }}>
                <li><strong>Status 100 (DECLINED):</strong> Transfer was declined due to insufficient funds or bank rejection</li>
                <li><strong>Status 0 (CANCELED):</strong> Transfer was canceled by the user, admin, or employer before completion</li>
                <li><strong>Status 200 (PENDING_OTP_CONFIRM):</strong> Transfer was created but never confirmed with OTP code</li>
                <li><strong>Status 300 (PENDING_TRANSFER):</strong> Waiting for admin to download and validate daily transfers (stuck in pending state)</li>
                <li><strong>Status 400 (PENDING_APPROVAL):</strong> Waiting for validation after admin downloaded daily transfers (stuck in pending state)</li>
              </ul>
              <div style={{ marginTop: '8px' }}>
                <strong>💡 Note:</strong> The status breakdown columns (Canceled %, Declined %, etc.) show the percentage of each failed status <strong>among all failed transfers</strong> (not as % of total transfers).
              </div>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#333' }}>Month</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Success Rate</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Total Transfers Attempted</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Successful</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Failed</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Failed %</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#ff4444', fontSize: '12px' }}>Canceled %</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#ff4444', fontSize: '12px' }}>Declined %</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#ff4444', fontSize: '12px' }}>Pending OTP %</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#ff4444', fontSize: '12px' }}>Pending Transfer %</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#ff4444', fontSize: '12px' }}>Pending Approval %</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Avg Amount</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Total Amount</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Velocity/Day</th>
                </tr>
              </thead>
              <tbody>
                {displayMonthlyData.map((row, idx) => (
                  <tr key={`${row.month}-${idx}`} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px', color: '#333', fontWeight: 500 }}>
                      {formatMonth(row.month)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#333', fontWeight: 500 }}>
                      {row.transferSuccessRate.toFixed(1)}%
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#333' }}>
                      {row.totalTransfers.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#4CAF50', fontWeight: 500 }}>
                      {row.successfulTransfers.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#ff4444', fontWeight: 500 }}>
                      {row.failedTransfers.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#ff4444', fontWeight: 500 }}>
                      {row.totalTransfers > 0 ? ((row.failedTransfers / row.totalTransfers) * 100).toFixed(1) : '0.0'}%
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#ff4444', fontSize: '12px' }}>
                      {(row.status0Percent || 0).toFixed(1)}%
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#ff4444', fontSize: '12px' }}>
                      {(row.status100Percent || 0).toFixed(1)}%
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#ff4444', fontSize: '12px' }}>
                      {(row.status200Percent || 0).toFixed(1)}%
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#ff4444', fontSize: '12px' }}>
                      {(row.status300Percent || 0).toFixed(1)}%
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#ff4444', fontSize: '12px' }}>
                      {(row.status400Percent || 0).toFixed(1)}%
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#333', fontWeight: 500 }}>
                      ₪{row.avgTransferAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#333', fontWeight: 500 }}>
                      ₪{row.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#333', fontWeight: 500 }}>
                      ₪{row.transferVelocity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
